import { UserManager, WebStorageStateStore, type UserManagerSettings } from 'oidc-client-ts';
import { runtimeEnv } from '@/lib/env';

/** Routes dédiées du flow : elles doivent exister dans le routeur ET dans les redirectUris du client. */
export const CALLBACK_PATH = '/callback';
export const SILENT_CALLBACK_PATH = '/silent-callback';
export const LOGIN_PATH = '/login';

/**
 * Un `UserManager` par realm (un realm Keycloak par tenant, cf. dev/keycloak/README.md §1).
 * Le realm n'est connu qu'après le lookup public tenant → realm, donc les managers sont créés à
 * la demande et mémorisés : deux onglets sur deux tenants gardent deux sessions distinctes.
 */
const managers = new Map<string, UserManager>();

function origin(): string {
  return typeof window === 'undefined' ? 'http://localhost:5173' : window.location.origin;
}

export function oidcSettings(realm: string): UserManagerSettings {
  const env = runtimeEnv();
  return {
    authority: `${env.oidcBaseUrl.replace(/\/+$/, '')}/realms/${realm}`,
    client_id: env.oidcClientId,
    // `graal-ui` est PUBLIC : pas de client_secret. PKCE S256 est imposé côté realm
    // (`pkce.code.challenge.method: S256`) et oidc-client-ts n'émet que du S256 en code flow.
    redirect_uri: `${origin()}${CALLBACK_PATH}`,
    // Piège payé sur recrutauto : sans silent_redirect_uri distinct, l'iframe de renouvellement
    // silencieux réutilise /callback, court-circuite le signinRedirectCallback interactif et
    // consomme deux fois le même code PKCE (« Code already used » côté Keycloak, double login).
    silent_redirect_uri: `${origin()}${SILENT_CALLBACK_PATH}`,
    post_logout_redirect_uri: `${origin()}${LOGIN_PATH}`,
    response_type: 'code',
    scope: 'openid profile email',
    automaticSilentRenew: true,
    // Jetons en sessionStorage : ils meurent avec l'onglet, et ne sont pas partagés entre onglets.
    userStore: new WebStorageStateStore({ store: window.sessionStorage }),
    stateStore: new WebStorageStateStore({ store: window.sessionStorage }),
    // Pas d'iframe de check-session : Keycloak 26 ne la sert plus par défaut et elle bruite la console.
    monitorSession: false,
    loadUserInfo: false,
  };
}

export function userManagerFor(realm: string): UserManager {
  const existing = managers.get(realm);
  if (existing) return existing;
  const manager = new UserManager(oidcSettings(realm));
  managers.set(realm, manager);
  return manager;
}

export function knownRealms(): string[] {
  return [...managers.keys()];
}

/** Tests uniquement. */
export function resetUserManagers(): void {
  for (const manager of managers.values()) manager.stopSilentRenew();
  managers.clear();
}
